// src/pages/admin/OrdersAdmin.jsx
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAdminOrders } from "../../api/orders";
import LoadingSpinner from "../../components/LoadingSpinner";
import { formatDateToDDMMYYYY } from "../../stores/utils";
import useDebounce from "../../stores/useDebounce";

export default function OrdersAdmin() {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Whenever the debounced search term changes, reset to page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const {
    data = { orders: [], totalPages: 0 },
    isLoading,
    isPreviousData,
  } = useQuery({
    queryKey: ["adminOrders", { page, pageSize, search: debouncedSearchTerm }],
    queryFn: () =>
      getAdminOrders({
        includeWarranty: false,
        page,
        pageSize,
        search: debouncedSearchTerm,
      }),
    keepPreviousData: true,
  });

  if (isLoading) return <LoadingSpinner />;

  const { orders, totalPages } = data;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Search & Pagination Controls */}
      <div className="flex items-center mb-4 space-x-4">
        <h1 className="text-2xl font-bold mb-4">Orders Management</h1>
        <input
          type="text"
          placeholder="Search by order no., reg no., name, email or contact…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="p-2 border rounded flex-grow"
        />
      </div>

      {/* Orders Table */}
      <table className="w-full table-auto border-collapse">
        <thead>
          <tr className="bg-surface">
            <th className="p-2 text-left">Order No.</th>
            <th className="p-2 text-left">Registration No.</th>
            <th className="p-2 text-left">Order Date</th>
            <th className="p-2 text-right">Amount (£)</th>
            <th className="p-2 text-left">Customer Name</th>
            <th className="p-2 text-left">Customer Email</th>
            <th className="p-2 text-left">Customer Address</th>
            <th className="p-2 text-left">Customer Contact</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o._id} className="border-t">
              <td className="p-2">{o.orderNumber}</td>
              <td className="p-2">{o.vehicle?.registration}</td>
              <td className="p-2">{formatDateToDDMMYYYY(o.orderDate)}</td>
              <td className="p-2 text-right">{o.amount.toLocaleString()}</td>
              <td className="p-2">
                {o.firstName} {o.lastName}
              </td>
              <td className="p-2">{o.email}</td>
              <td className="p-2">
                {`${o.address.street}, ${o.address.city}, ${o.address.postcode}, ${o.address.country}`}
              </td>
              <td className="p-2">{o.contactNumber}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="flex justify-end items-center mt-4 space-x-4">
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={page === 1}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Prev
        </button>

        <span>
          Page {page} of {totalPages}
        </span>

        <button
          onClick={() =>
            setPage((p) => (!isPreviousData && p < totalPages ? p + 1 : p))
          }
          disabled={page === totalPages}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
